import React, { useState } from 'react';

const CompanyPicker = ({ 
  companies, 
  selectedId, 
  onSelect, 
  onCancel,
  onSetPrimary,
  title = 'Select Company',
  submitLabel = 'Continue',
  loading = false,
  error = '' 
}) => {
  const [selected, setSelected] = useState(selectedId || companies[0]?.id);
  const [settingPrimary, setSettingPrimary] = useState(null);

  const handleSubmit = () => {
    if (selected) onSelect(selected);
  };

  const handleSetPrimary = async (companyId, e) => {
    e.stopPropagation();
    if (!onSetPrimary) return;
    setSettingPrimary(companyId);
    try {
      await onSetPrimary(companyId);
    } finally {
      setSettingPrimary(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {companies.length > 1 
              ? `You have access to ${companies.length} companies`
              : 'Confirm your company'}
          </p>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-3 rounded">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-3 max-h-64 overflow-y-auto">
          {companies.map((company) => (
            <label
              key={company.id}
              className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                selected === company.id
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name="company"
                value={company.id}
                checked={selected === company.id}
                onChange={() => setSelected(company.id)}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
              />
              <div className="ml-3 flex-1">
                <p className="font-medium text-gray-900">{company.name}</p>
                {company.role && (
                  <p className="text-sm text-gray-500">{company.role}</p>
                )}
              </div>
              {company.is_primary && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded mr-2">Default</span>
              )}
              {company.is_current && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Current</span>
              )}
              {onSetPrimary && !company.is_primary && (
                <button
                  type="button"
                  onClick={(e) => handleSetPrimary(company.id, e)}
                  disabled={settingPrimary === company.id}
                  className="ml-2 text-xs text-primary hover:text-primary-hover px-2 py-1 disabled:opacity-50"
                >
                  {settingPrimary === company.id ? '...' : 'Set as default'}
                </button>
              )}
            </label>
          ))}
        </div>

        <div className="mt-6 flex gap-3">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || !selected}
            className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-hover disabled:opacity-50"
          >
            {loading ? 'Loading...' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyPicker;
